import { CountryProfile, CountryCode } from '@/store/ui';
import { countries } from '@/constants';
import { CmsLink, CustomerDeliveryAddress, Restaurant, OrderWithContext, CustomerProfile } from '@/models';
import { AppState } from '@/store';
import { OrderService } from '@/services/order/order.service';
import { ConfigService } from '@/services/config/config.service';
import { UiService } from '@/services/ui/ui.service';
import { getI18nInstance } from '@/i18n';

export interface PersistCrossDomainDataOptions {
  data: PersistCrossDomainData;
  restaurant: Restaurant | null;
  sessionFrameUrl: string;
  shouldRedirect: boolean;
  redirectQueryParams?: { [key: string]: string };
}

export interface PersistCrossDomainData {
  order?: OrderWithContext | null;
  deliveryAddress?: CustomerDeliveryAddress | null;
  jwt: string;
}

export interface CountryNameOptions {
  useShortName?: boolean | undefined,
  includePrefix?: boolean | undefined
}

export function getCountryByHostname(hostname: string): CountryProfile {
  const topLevelDomain = hostname.substring(hostname.lastIndexOf('.') + 1);
  const countryCode = topLevelDomain === 'com' || hostname.includes('localhost') ? 'us' : topLevelDomain.toLowerCase();

  return countries[countryCode] || countries.us;
}

export function updateBrowserUrl(location: any, newTopLevelDomain: string, queryParams?: { [key: string]: string }): void {
  const protocol = location.protocol;
  const hostname = location.hostname;
  const newHostName = hostname.substring(0, hostname.lastIndexOf('.'));
  const port = location.port ? ':' + location.port : '';

  let fromFac = false;
  let queryString = '';
  if (queryParams) {
    queryString = '?';
    Object.keys(queryParams).forEach(key => {
      queryString += `${key}=${queryParams[key]}&`;

      if (key == 'fromFac' && queryParams[key] == 'true') {
        fromFac = true;
      }
    });
  }
  // Always redirect to the Order page if coming from FAC pill, otherwise go to the root
  const orderPageDestination = fromFac ? '/order/' : '';

  const newUrl = `${protocol}//${newHostName}${newHostName == 'localhost' ? '' : newTopLevelDomain}${port}${orderPageDestination}${queryString}`;
  location.replace(newUrl);
}

export function getTopLevelDomainByCountryCode(countryCode: string): string {
  return countries[countryCode.toLowerCase()]?.topLevelDomain ?? '.com';
}

export function getCountryCodeByTopLevelDomain(hostName: string = window.location.hostname): string {
  return getCountryByHostname(hostName).countryCode;
}

export function getCountryName(countryCode: string, { useShortName = false, includePrefix = false }: CountryNameOptions = { useShortName: false, includePrefix: false }): string {
  const i18n = getI18nInstance();
  const countryLocale = i18n.t(`countries.${countryCode.toLocaleUpperCase()}`);

  if (typeof countryLocale !== 'object') {
    return '';
  }

  const countryName = useShortName ? countryLocale.shortName : countryLocale.name;
  const prefix = includePrefix && !!countryLocale.namePrefix ? `${countryLocale.namePrefix} ` : '';

  return `${prefix}${countryName}`;
}

export function parseCountryCode(value: string, defaultValue: CountryCode = CountryCode.US): CountryCode {
  const refinedValue = value.trim().toUpperCase();
  return refinedValue in CountryCode ? refinedValue as CountryCode : defaultValue;
}

export function persistCrossDomainData(options: PersistCrossDomainDataOptions | null) {
  if (!options) {
    return;
  }

  const { data, shouldRedirect, restaurant, sessionFrameUrl, redirectQueryParams } = options;

  const iframe = (document.getElementById('sessionFrame') as unknown) as HTMLIFrameElement;
  if (iframe) {
    // Send a message to the iframe to store data
    const win = iframe.contentWindow;
    if (win) {
      if (shouldRedirect) {
        const messageHandler = (event: any) => {
          if (event.origin !== sessionFrameUrl) {
            return;
          }

          if (event.data && JSON.parse(event.data)) {
            window.removeEventListener('message', messageHandler);
            updateBrowserUrl(location, getTopLevelDomainByCountryCode((restaurant && restaurant.address.countryCode) || ''), redirectQueryParams);
          }
        };

        // overwrite onmessage set from cmg-app.ts onLoadSessionIFrame so it does not delete the data after the read
        window.onmessage = messageHandler;
      }

      // first save the customer data, then send a message to get it to trigger the redirect in order to ensure it was saved
      win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'set', data }), sessionFrameUrl);
      if (shouldRedirect) {
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'get' }), sessionFrameUrl);
      }
    }
  }
}

export function shouldSwitchDomain(currentRestaurant: Restaurant | null): boolean {
  const countryCode = getCountryCodeByTopLevelDomain();
  if (currentRestaurant && countryCode != currentRestaurant.address.countryCode) {
    return true;
  }

  return false;
}

export function startSwitchDomainProcess(state: AppState, uiSerivice: UiService, orderService: OrderService, configService: ConfigService) {
  // If it is a guest and restaurant country does not match domain, redirect
  // If it is an authenticated user and profile country does not match domain, show countrySwitchAlert modal before redirect
  const selectedRestaurant = state.restaurant.selectedRestaurant;
  const shouldShowCountrySwitchAlertModal = ((getCountryCodeByTopLevelDomain() !== selectedRestaurant?.address.countryCode) && (state.customer.profile && !state.customer.profile?.isGuest)) ?? false;

  persistCrossDomainData(getPersistCrossDomainDataOptions(state, orderService, configService));

  if (shouldShowCountrySwitchAlertModal) {
    uiSerivice.setModalVisibility('countrySwitchAlertModal', true);
    uiSerivice.firstFocus();
  }
}

export function getPersistCrossDomainDataOptions(state: AppState, orderService: OrderService, configService: ConfigService): PersistCrossDomainDataOptions | null {
  if (!state.restaurant.selectedRestaurant) {
    return null;
  }

  const customer = state.customer.profile && !state.customer.profile.isGuest ? state.customer.profile : null;
  const selectedRestaurant = state.restaurant.selectedRestaurant;
  const shouldShowCountrySwitchAlertModal = (customer && !customer.isGuest && selectedRestaurant && (getCountryCodeByTopLevelDomain() !== selectedRestaurant.address.countryCode)) ?? false;
  const order = orderService.getCurrentOrder();
  const redirectQueryParams: { [key: string]: string } = {};
  redirectQueryParams.fromFac = 'true';

  if (orderService.isPickingUp()) {
    redirectQueryParams.restaurant = state.restaurant.selectedRestaurant.restaurantNumber.toString();
    if (shouldShowCountrySwitchAlertModal) {
      redirectQueryParams.hideToast = 'true';
    }
  } else {
    if (!shouldShowCountrySwitchAlertModal) {
      redirectQueryParams.showDeliveryToast = 'true';
    }
  }

  if (order) {
    redirectQueryParams.hasBag = 'true';
  }

  return {
    data: {
      deliveryAddress: state.order.selectedDeliveryAddress,
      order,
      jwt: state.customer.jwt,
    },
    sessionFrameUrl: configService.getSessionFrameUrl(),
    restaurant: state.restaurant.selectedRestaurant,
    shouldRedirect: shouldShowCountrySwitchAlertModal ? false : true,
    redirectQueryParams,
  };
}

export function shouldShowLink(siteCountryCode: string, profileCountryCode: string, isCustomerLoggedIn: boolean) {
  return (link: CmsLink) => {
    const linkText = link.type == 'text' ? link.text : '';
    const shouldShow: { [key: string]: boolean } = {
      Menu: true,
      Order: true,
      Catering: true,
      Rewards: isCustomerLoggedIn || isLoyaltyEnabled(siteCountryCode),
      'Our Values': true,
      Nutrition: true,
    };
    return shouldShow[linkText];
  };
}

export function shouldShowRewards(countryCode: string): boolean {
  return isLoyaltyEnabled(countryCode);
}

export function isLoyaltyEnabled(countryCode: string): boolean {
  return countries[countryCode.toLowerCase()]?.isLoyaltyEnabled || false;
}

export function isUserLoyaltyEligible(siteCountryCode: string, userProfileCountry: string): boolean {
  return isLoyaltyEnabled(siteCountryCode) && userProfileCountry === siteCountryCode;
}

export function isSustainabilityEnabled(countryCode: string): boolean {
  return countries[countryCode.toLowerCase()]?.isSustainablityEnabled || false;
}

// This is to check if user is signed in as a Canadian or a guest that is in Canada.
export function isCustomerCanadian(customer: CustomerProfile | null, state: AppState): boolean | null {
  return (customer && customer.country == CountryCode.CA) || state.ui.countryProfile.countryCode == CountryCode.CA;
}

export function isAutoSubscribesToLoyaltyEmail(countryCode?: string): boolean {
  return countries[(countryCode ?? '').toLowerCase()]?.isAutoSubscribesToLoyaltyEmail ?? false;
}

export function isAutoSubscribesToMarketingEmail(countryCode?: string): boolean {
  return countries[(countryCode ?? '').toLowerCase()]?.isAutoSubscribesToMarketingEmail ?? false;
}
